import React, { useState } from "react";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Button, Stack, Typography } from "@mui/material";
import { usePrompt } from "hooks";
import { differenceWith, intersectionWith } from "lodash";
import { useMeasureSelect } from "store";
import { CurrencyType, IUpdateMeasuresValues, QuickViewMeasureData } from "store/types";

import { currencySign } from "../../../../constants";
import { AddMeasureDataForm } from "./AddMeasureDataForm";
import { CharacteristicForm } from "./CharacteristicForm";
import { MeasureDataItem } from "./MeasureDataItem";
import { ReplaceMeasureDataPrompt } from "./ReplaceMeasureDataPrompt";
import { TotalRow } from "./TotalRow";

const initialState: State = {
  characteristicValue: "",
  measureData: [],
  assetId: null,
  addValueActive: false,
  newMeasureData: {
    date: "",
    value: "",
    description: "",
    selectedCurrency: "USD"
  }
};

export type State = {
  characteristicValue: string;
  measureData: QuickViewMeasureData[];
  assetId: number | null;
  addValueActive: boolean;
  newMeasureData: {
    date: string;
    value: string;
    selectedCurrency?: CurrencyType;
    description: string;
  };
};

export type UpdateMeasureData = (
  data: Pick<QuickViewMeasureData, "measure_data_id"> &
    Partial<Pick<QuickViewMeasureData, "value" | "currency" | "description" | "date" | "edited" | "postfix">>
) => void;

interface Props {
  columnKey: string;
  columnType: "measure" | "characteristic";
  isAbsolute: boolean;
  isNegative: boolean;
  measureId?: number;
  currency?: CurrencyType;
  is_total?: boolean;
  value?: string;
  measureData?: QuickViewMeasureData[];
  dealId?: number;
  investorId?: number;
  roundId?: number;

  characteristicDataType?: "number" | "text" | "percentage" | "currency" | "asset" | "date"; //type of characteristic to define input type
  assetId?: number;
  subAssetId?: number;
  container?: HTMLElement | null;

  onSubmit: (data: Pick<State, "characteristicValue" | "assetId"> & { measureData: IUpdateMeasuresValues }) => void;
  onCancel: () => void;
}

const getDate = (date: string) => date.slice(0, 10);

const propsToState = (props: Props): State => ({
  ...initialState,
  characteristicValue: props.value && props.value !== "-" ? props.value : "",
  measureData: props.measureData || [],
  assetId: props.subAssetId || props.assetId || null,
  addValueActive: false,
  newMeasureData: {
    date: "",
    value: "",
    description: "",
    selectedCurrency: props.currency || "USD"
  }
});

export const EditTableCell: React.FC<Props> = (props) => {
  const {
    columnKey,
    columnType,
    isAbsolute,
    isNegative,
    measureId,
    currency,
    is_total,
    value,

    characteristicDataType,
    assetId,
    subAssetId,
    container,

    onSubmit,
    onCancel
  } = props;

  const [state, setState] = useState<State>(propsToState(props));

  const { characteristicValue, measureData, assetId: assetIdState, addValueActive, newMeasureData } = state;

  const measure = useMeasureSelect(measureId || 0);
  const openPrompt = usePrompt();

  const isCurrency = Boolean(measure?.is_currency);

  const setCharacteristicValue = (value: string) => {
    setState({ ...state, characteristicValue: value });
  };

  const setAssetIdState = (value: number | null) => {
    setState({ ...state, assetId: value });
  };

  const setMeasureData = (
    newStateOrCb: State["measureData"] | ((prevState: State["measureData"]) => State["measureData"])
  ) => {
    setState({
      ...state,
      measureData: typeof newStateOrCb === "function" ? newStateOrCb(state.measureData) : newStateOrCb
    });
  };

  const setNewMeasureData = async (
    newStateOrCb: State["newMeasureData"] | ((prevState: State["newMeasureData"]) => State["newMeasureData"])
  ) => {
    const newMeasureData = typeof newStateOrCb === "function" ? newStateOrCb(state.newMeasureData) : newStateOrCb;

    // Basic set state to update the newMeasureData
    setState({ ...state, newMeasureData });

    // The flow to check if new measureDate date matches any existing date
    if (newMeasureData.date && newMeasureData.value && newMeasureData.selectedCurrency) {
      // Try to find if any existing measureData has the same date
      const indexForReplace = measureData.findIndex((md) => getDate(md.date) === getDate(newMeasureData.date));
      const matchedMeasureData = measureData.find((md) => getDate(md.date) === getDate(newMeasureData.date));

      // If the date matches an existing date, open a prompt to ask user for replace
      if (matchedMeasureData && indexForReplace >= 0) {
        const replaceApproved = await openPrompt({
          Component: ReplaceMeasureDataPrompt,
          container,
          props: {
            date: matchedMeasureData.date,
            currentValue: String(matchedMeasureData.value).replace(/,/g, ""),
            currentCurrency: matchedMeasureData.currency,
            nextValue: newMeasureData.value.replace(/,/g, ""),
            nextCurrency: newMeasureData.selectedCurrency
          }
        });

        // If the user approved the replace, update the existing measureData
        if (replaceApproved) {
          // Create a copy of the measureData to avoid mutating the original state
          const measureDataCopy = [...measureData];

          // Update the existing measureData with the new values
          measureDataCopy[indexForReplace] = {
            ...matchedMeasureData,
            value: Number(newMeasureData.value),
            date: newMeasureData.date,
            currency: newMeasureData.selectedCurrency,
            postfix: currencySign[newMeasureData.selectedCurrency],
            description: newMeasureData.description
          };

          // Additional set state to update the measureData and reset the addValueActive
          setState({
            ...state,
            measureData: measureDataCopy,
            ...(replaceApproved && { addValueActive: false, newMeasureData: initialState.newMeasureData })
          });
        }
      }
    }
  };

  const updateMeasureData: UpdateMeasureData = async (data) => {
    let measureDataCopy = [...measureData];

    // Check if the measureData exists
    const targetMeasureData = measureDataCopy.find((md) => md.measure_data_id === data.measure_data_id);
    if (!targetMeasureData) return;

    const updatedMeasureData = { ...targetMeasureData, ...data };

    // Update the measureData with the new values
    measureDataCopy = measureDataCopy.map((item) =>
      item.measure_data_id === data.measure_data_id ? updatedMeasureData : item
    );

    // Run the flow only if the date was changed for measureData replacing
    if (data.date) {
      // Try to find if any existing measureData has the same date (with different id)
      const indexForReplace = measureDataCopy.findIndex(
        (md) => getDate(md.date) === getDate(data.date!) && md.measure_data_id !== data.measure_data_id
      );

      if (indexForReplace >= 0) {
        const measureDataForReplace = measureDataCopy[indexForReplace];

        // Open a prompt to ask user for replace value with the same date to the new value.
        // The id of the measureData will be of item that took the date first.
        // The updated measureData will be filtered from the array, it's values will be set to an existing measureData.
        const replaceApproved = await openPrompt({
          Component: ReplaceMeasureDataPrompt,
          container,
          props: {
            date: measureDataForReplace?.date,
            currentValue: String(measureDataForReplace?.value).replace(/,/g, ""),
            currentCurrency: measureDataForReplace?.currency,
            nextValue: String(updatedMeasureData.value).replace(/,/g, ""),
            nextCurrency: updatedMeasureData.currency
          }
        });

        // If the user approved the replace, update the existing measureData
        if (replaceApproved) {
          // Record new values to the existing measureData
          measureDataCopy[indexForReplace] = {
            ...measureDataForReplace,
            value: Number(updatedMeasureData.value),
            date: updatedMeasureData.date,
            currency: updatedMeasureData.currency,
            postfix: currencySign[updatedMeasureData.currency],
            description: updatedMeasureData.description
          };

          // Remove the measureData with the same date
          measureDataCopy = measureDataCopy.filter((item) => item.measure_data_id !== data.measure_data_id);
        }
      }
    }

    setMeasureData(measureDataCopy);
  };

  const deleteMeasureData = (measure_data_id: number) => {
    setMeasureData((prev) => prev.filter((item) => item.measure_data_id !== measure_data_id));
  };

  const handleSubmit = () => {
    const measureDataToSend: IUpdateMeasuresValues = {
      create: [],
      update: [],
      delete: []
    };

    if (columnType === "measure" && measureId && props.measureData) {
      // searching for deleted values
      measureDataToSend.delete = differenceWith(
        props.measureData,
        state.measureData || [],
        (obj1, obj2) => obj1.measure_data_id === obj2.measure_data_id
      ).map((item) => {
        return item.measure_data_id;
      });

      //search for objects that have changed
      measureDataToSend.update = intersectionWith(state.measureData, props.measureData, (obj1, obj2) => {
        return (
          obj1.measure_data_id === obj2.measure_data_id &&
          (obj1.value !== obj2.value ||
            obj1.currency !== obj2.currency ||
            obj1.date !== obj2.date ||
            obj1.description !== obj2.description)
        );
      }).map((item) => {
        return {
          ...item,
          validation_id: 1
        };
      });

      if (
        (newMeasureData.value || newMeasureData.value === "0") &&
        newMeasureData.date &&
        newMeasureData.selectedCurrency
      ) {
        measureDataToSend.create.push({
          validation_id: 1,
          currency: newMeasureData.selectedCurrency,
          date: newMeasureData.date,
          is_total: false,
          measure_id: measureId,
          postfix: currencySign[newMeasureData.selectedCurrency],
          value: Number(newMeasureData.value),
          description: newMeasureData.description,
          round_id: props.roundId,
          deal_id: props.dealId,
          investor_id: props.investorId,
          is_date_editable: true
        });
      }
    }

    onSubmit({
      characteristicValue: state.characteristicValue,
      assetId: state.assetId,
      measureData: measureDataToSend
    });
  };

  return (
    <Stack
      sx={{
        width: 430,
        p: "20px",
        boxShadow: "-2px 2px 15px -2px rgba(0,0,0,.25)",
        borderRadius: "8px",
        bgcolor: "white",
        gap: "6px"
      }}
    >
      <Typography sx={{ color: "#0a3142", fontSize: "16px", fontWeight: 500 }}>{columnKey}</Typography>
      <Stack sx={{ gap: "6px" }}>
        {columnType === "measure" && !addValueActive && (
          <Button
            onClick={() => setState({ ...state, addValueActive: true })}
            startIcon={<AddCircleOutlineIcon />}
            sx={{ color: "#26a6ae", alignSelf: "flex-start" }}
          >
            Add Value
          </Button>
        )}
        {addValueActive && (
          <AddMeasureDataForm
            columnType={columnType}
            isAbsolute={isAbsolute}
            isNegative={isNegative}
            currency={currency}
            state={newMeasureData}
            setState={setNewMeasureData}
            isCurrency={isCurrency}
            container={container}
          />
        )}
        {characteristicDataType && (
          <CharacteristicForm
            characteristicDataType={characteristicDataType}
            onChange={setCharacteristicValue}
            value={characteristicValue}
            container={container}
            {...(subAssetId
              ? {
                  // Sub-asset class mode: pass original assetId as parent, assetIdState as subAssetId
                  assetId: assetId || undefined,
                  subAssetId: assetIdState || undefined,
                  isSubAssetClassMode: true
                }
              : {
                  // Asset class mode: pass assetIdState as assetId
                  assetId: assetIdState || undefined
                })}
            onChangeAssets={setAssetIdState}
          />
        )}
        {is_total && value && currency && <TotalRow value={value} currency={currency} />}
        {columnType === "measure" && Boolean(measureData.length) && (
          <Stack sx={{ gap: "12px", maxHeight: 407, overflowY: "auto" }}>
            {measureData
              .sort((a, b) => {
                return new Date(b.date).getTime() - new Date(a.date).getTime();
              })
              .map((item) => (
                <MeasureDataItem
                  key={item.measure_data_id}
                  data={item}
                  updateMeasureData={updateMeasureData}
                  deleteMeasureData={deleteMeasureData}
                  isAbsolute={isAbsolute}
                  isNegative={isNegative}
                  isCurrency={isCurrency}
                  container={container}
                  isDateEditable={item.is_date_editable}
                />
              ))}
          </Stack>
        )}
      </Stack>
      <Stack
        sx={{
          flexDirection: "row",
          justifyContent: "flex-end",
          pt: "20px",
          pr: "25px",
          gap: "10px",
          ...(!measureData.length && { borderTop: "1px solid #dae0e2" })
        }}
      >
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} sx={{ backgroundColor: "#071f35" }}>
          Save
        </Button>
      </Stack>
    </Stack>
  );
};
